import { API_URL } from '@/utils/constants';

import { TrackingEvent, TrackingRequest } from './TrackingRequest';

// Field-level tracking takes a session from roughly fifteen events to well over
// a hundred. One fire-and-forget request per event does not survive that, so
// events are buffered and flushed in batches.
const MAX_BUFFER = 10;
const IDLE_FLUSH_MS = 5000;
const TRACKING_PATH = '/api/tracking';

type SessionFields = Omit<TrackingRequest, 'events'>;

let buffer: TrackingEvent[] = [];
let pendingSession: SessionFields | null = null;
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;

function endpoint(): string {
    return `${API_URL ?? ''}${TRACKING_PATH}`;
}

function clearIdleTimer(): void {
    if (idleTimer) {
        clearTimeout(idleTimer);
        idleTimer = null;
    }
}

// Deliberately not the shared axios client. That instance carries a response
// interceptor which opens the login modal on a 401 and attaches an auth header
// on every call — telemetry must never be able to interrupt an applicant, so it
// goes out on a bare fetch instead.
//
// keepalive lets the request outlive the page, which matters for the final
// flush. Errors are swallowed: a tracking failure must never surface to a
// customer or block an application.
//
// The body is JSON but goes out as text/plain, a CORS-safelisted content type.
// With application/json the browser must preflight the cross-origin request,
// and a preflight cannot complete once the page is unloading — so the beacon
// was accepted (sendBeacon returned true) and then silently dropped. In
// production the page-hide flush landed for 0.2% of Chrome sessions and for no
// engaged session at all (2 Sep 2026). The API parses both types.
const CONTENT_TYPE = 'text/plain';

function send(payload: TrackingRequest, useBeacon: boolean): void {
    const body = JSON.stringify(payload);

    try {
        if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const blob = new Blob([body], { type: CONTENT_TYPE });
            const queued = navigator.sendBeacon(endpoint(), blob);
            if (queued) return;
        }

        void fetch(endpoint(), {
            method: 'POST',
            headers: { 'Content-Type': CONTENT_TYPE },
            body,
            keepalive: true,
        }).catch(() => undefined);
    } catch (error) {
        /* telemetry is best effort */
    }
}

export function flushTrackingBuffer(useBeacon = false): void {
    clearIdleTimer();

    if (!buffer.length || !pendingSession) return;

    const events = buffer;
    buffer = [];

    send({ ...pendingSession, events }, useBeacon);
}

// Runs immediately before the final flush, so a caller can queue one last event
// and have it included rather than left in a buffer that is never sent again.
// A hook rather than a direct call because the emitter imports this module, and
// calling back into it would be circular.
let beforeFinalFlush: (() => void) | null = null;

export function registerBeforeFinalFlush(hook: () => void): void {
    beforeFinalFlush = hook;
}

function finalFlush(): void {
    try {
        beforeFinalFlush?.();
    } catch (error) {
        /* never let a hook stop the flush */
    }

    flushTrackingBuffer(true);
}

// The last events before an abandon are exactly the ones that identify where
// the abandon happened, so the page-hide flush is the one that matters most.
// visibilitychange fires reliably on mobile where unload does not.
function bindLifecycleListeners(): void {
    if (listenersBound || typeof document === 'undefined') return;
    listenersBound = true;

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') finalFlush();
    });

    window.addEventListener('pagehide', () => finalFlush());
}

export function queueTrackingEvent(session: SessionFields, event: TrackingEvent): void {
    bindLifecycleListeners();

    // The newest session fields win, so values that only exist later in a
    // journey — the application id, the mobile number — travel with the next
    // batch and land on the document the earlier events already created.
    pendingSession = session;
    buffer.push(event);

    if (buffer.length >= MAX_BUFFER) {
        flushTrackingBuffer();
        return;
    }

    clearIdleTimer();
    idleTimer = setTimeout(() => flushTrackingBuffer(), IDLE_FLUSH_MS);
}

// A session value that arrived after the last event was queued — the quick
// calculator's estimate lands a couple of seconds after the input change that
// asked for it — would otherwise wait for the next event, or ride the final
// flush alone. Replaces the fields the buffered batch will go out with.
export function refreshPendingSession(session: SessionFields): void {
    pendingSession = session;
}

// Called on screen change so a screen's events are not still sitting in the
// buffer while the applicant is already somewhere else.
export function flushOnScreenChange(): void {
    flushTrackingBuffer();
}

// Test seam only.
export function resetTrackingBuffer(): void {
    clearIdleTimer();
    buffer = [];
    pendingSession = null;
    beforeFinalFlush = null;
}
